/**
 * Dates and numbers, formatted the same way on the server and in the browser.
 *
 * `toLocaleDateString()` and `Intl.DateTimeFormat(undefined, …)` resolve to
 * whatever default the runtime carries: `en-US` in the Debian container,
 * the visitor's own locale in their browser. Every server-rendered date
 * therefore came back different on hydration ("Sep 01, 2025" against
 * "01 Sept 2025"), and a text mismatch makes React discard the entire
 * server tree and re-render the page on the client - which is how the
 * marketing pages and the dashboard were both throwing React #418 in
 * production. Pinning the locale is what makes the two sides agree.
 *
 * The zone is pinned for the same reason: with the runtime default, a
 * timestamp near midnight lands on different days on the two sides. UTC over
 * the visitor's own zone is a deliberate trade - reading local times back
 * would mean sending the zone to the server as a client hint, and these are
 * audit timestamps, not appointments.
 */

const LOCALE = 'en-GB'

const DATE_DEFAULTS: Intl.DateTimeFormatOptions = {
	day: '2-digit',
	month: 'short',
	year: 'numeric',
}

const DATE_TIME_DEFAULTS: Intl.DateTimeFormatOptions = {
	...DATE_DEFAULTS,
	hour: '2-digit',
	minute: '2-digit',
}

export function formatDate(
	value: Date | string | number,
	options: Intl.DateTimeFormatOptions = DATE_DEFAULTS,
): string {
	return new Intl.DateTimeFormat(LOCALE, {
		timeZone: 'UTC',
		...options,
	}).format(new Date(value))
}

export function formatDateTime(
	value: Date | string | number,
	options: Intl.DateTimeFormatOptions = DATE_TIME_DEFAULTS,
): string {
	return formatDate(value, { ...DATE_TIME_DEFAULTS, ...options })
}

export function formatNumber(
	value: number,
	options?: Intl.NumberFormatOptions,
): string {
	return new Intl.NumberFormat(LOCALE, options).format(value)
}

/**
 * A duration in milliseconds, as hours/minutes/seconds.
 *
 * Larger units are dropped when they are zero, but never the smaller ones
 * once a larger one is present: an hour and thirty-nine seconds reads
 * "1h 0m 39s", not "1h 39s", so the shape of the string tells you its
 * magnitude without having to read the suffixes. Unit style over "1:00:39"
 * because "57:39" is ambiguous between mm:ss and hh:mm.
 *
 * Days are deliberately not a unit - a scan that ran that long is a fault to
 * read as "26h", not a duration to read as "1d 2h".
 */
export function formatDuration(ms: number): string {
	// Clamped rather than thrown on: callers guard for an absent duration
	// before they get here, so a negative or NaN gap is a clock artefact,
	// and "0s" is the least misleading thing to print for one.
	const totalSeconds = Number.isFinite(ms) && ms > 0 ? Math.round(ms / 1000) : 0

	const hours = Math.floor(totalSeconds / 3600)
	const minutes = Math.floor((totalSeconds % 3600) / 60)
	const seconds = totalSeconds % 60

	if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`
	if (minutes > 0) return `${minutes}m ${seconds}s`
	return `${seconds}s`
}
